import React, { useState } from 'react';
import SEO from './SEO';
import SearchForm from './SearchForm';
import AirlineCallPopup from './AirlineCallPopup';

const TFN = '+1 (888) 778-8606';
const TEL = 'tel:+18887788606';

const DEFAULT_WHY_BOOK = (name) => [
  { icon: '💰', title: 'Best Fare Search', desc: `We compare all available ${name} fares — published, web-only, and consolidator — to find your lowest price.` },
  { icon: '📞', title: '24/7 Live Agents', desc: 'Real travel experts answer your call any time, day or night. No bots, no hold music, just fast help.' },
  { icon: '🔄', title: 'Free Change Assistance', desc: `Need to reschedule? We handle ${name} flight change requests on your behalf at no additional service charge.` },
  { icon: '🪑', title: 'Seat Selection Help', desc: `We assist with seat upgrades, cabin selection, and special meal requests on your ${name} booking.` },
  { icon: '🌐', title: 'Loyalty Program Support', desc: `Guidance on earning and redeeming miles, status matching, and upgrade awards on ${name}.` },
  { icon: '🛡️', title: 'Booking Protection', desc: 'We monitor your itinerary for schedule changes and proactively notify you of any disruptions.' },
];

export default function AirlineDetailPage({ airline }) {
  const [openFaq, setOpenFaq] = useState(null);
  const {
    code,
    name,
    canonical,
    tag,
    heroGradient,
    accentClass,
    statColor,
    hubBoxGradient,
    ctaGradient,
    logo,
    h1First,
    h1Second,
    subtitle,
    originAirport,
    destinationAirport,
    stats,
    aboutParagraphs,
    aboutBullets,
    hubs,
    hubsTitle = 'Key Hubs',
    cabins,
    cabinsHeading,
    cabinsSubheading,
    routes,
    routesHeading,
    routesSubheading,
    ctaHeading,
    ctaDesc,
    whyBook,
    faqs,
    seoTitle,
    seoDescription,
    seoKeywords,
    disclaimerName,
  } = airline;

  const today = new Date();
  today.setDate(today.getDate() + 14);
  const date = today.toISOString().split('T')[0];
  const searchUrl = `/flights?origin=${originAirport}&destination=${destinationAirport}&date=${date}&passengers=1&class=economy`;

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: `${name} Flights – Book & Save | TheAirlineFares`,
    url: `https://theairlinefares.com${canonical}`,
    description: seoDescription,
    publisher: {
      '@type': 'Organization',
      name: 'TheAirlineFares',
      url: 'https://theairlinefares.com',
      telephone: TFN,
    },
  };

  const faqJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map(f => ({
      '@type': 'Question',
      name: f.q,
      acceptedAnswer: { '@type': 'Answer', text: f.a },
    })),
  };

  const whyBookItems = whyBook || DEFAULT_WHY_BOOK(name);

  return (
    <div className="bg-white">
      <AirlineCallPopup name={name} logo={logo} code={code} />

      {/* Trademark Disclaimer */}
      <div className="bg-amber-50 border-b border-amber-100 py-2.5 px-4 text-center text-xs text-amber-800">
        <strong>Disclaimer:</strong> TheAirlineFares is an independent travel agency. &quot;{disclaimerName}&quot; and its logo are trademarks of their respective owner. We are not affiliated with or endorsed by {disclaimerName}.
      </div>

      <SEO
        title={seoTitle}
        canonical={canonical}
        description={seoDescription}
        keywords={seoKeywords}
        jsonLd={[jsonLd, faqJsonLd]}
      />

      {/* Hero */}
      <div className={`relative bg-gradient-to-br ${heroGradient} text-white overflow-hidden`}>
        <div className="absolute inset-0 opacity-10"
          style={{ backgroundImage: "url(\"data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E\")" }}
        />
        <div className="relative max-w-5xl mx-auto px-4 pt-12 pb-10 sm:pt-16 sm:pb-12">
          <div className="flex flex-col sm:flex-row items-center sm:items-start gap-5 mb-8">
            <div className="bg-white rounded-2xl p-3 shadow-xl shrink-0">
              <img src={logo.src} alt={logo.alt} width={logo.width || 200} height={logo.height || 67} className="block" />
            </div>
            <div className="text-center sm:text-left">
              <div className="inline-flex items-center gap-2 bg-white/10 border border-white/20 rounded-full px-3 py-1 text-xs font-medium mb-3">
                ✈️ {tag} · IATA: {code}
              </div>
              <h1 className="text-3xl sm:text-4xl md:text-5xl font-extrabold leading-tight">
                {h1First}<br className="hidden sm:block" />
                <span className={accentClass}>{h1Second}</span>
              </h1>
              <p className="text-white/80 text-base sm:text-lg mt-3 max-w-xl">{subtitle}</p>
            </div>
          </div>

          <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-2xl p-5 sm:p-7">
            <p className="text-white/70 text-sm font-medium mb-4">Search {name} Flights</p>
            <SearchForm initialValues={{ origin: originAirport }} />
          </div>

          <div className="mt-5 text-center">
            <p className="text-white/70 text-sm">
              Prefer to book by phone?{' '}
              <a href={TEL} className="text-white font-bold hover:text-white/80 underline underline-offset-2">Call {TFN}</a>
              {' '}— available 24/7
            </p>
          </div>
        </div>
      </div>

      {/* Stats strip */}
      <div className="bg-white border-b border-gray-100 py-6 px-4">
        <div className="max-w-4xl mx-auto grid grid-cols-2 sm:grid-cols-4 gap-4 text-center">
          {stats.map(s => (
            <div key={s.label}>
              <p className={`text-2xl font-extrabold ${statColor}`}>{s.value}</p>
              <p className="text-gray-500 text-sm">{s.label}</p>
            </div>
          ))}
        </div>
      </div>

      {/* About */}
      <section className="max-w-5xl mx-auto px-4 py-12 sm:py-16">
        <div className="grid md:grid-cols-2 gap-8 items-center">
          <div>
            <h2 className="text-2xl sm:text-3xl font-extrabold text-gray-900 mb-4">About {name}</h2>
            {aboutParagraphs.map((p, i) => (
              <p key={i} className="text-gray-600 leading-relaxed mb-4" dangerouslySetInnerHTML={{ __html: p }} />
            ))}
            <ul className="space-y-2">
              {aboutBullets.map(item => (
                <li key={item} className="flex items-start gap-2 text-sm text-gray-700">
                  <span className={`${statColor} font-bold mt-0.5`}>✓</span> {item}
                </li>
              ))}
            </ul>
          </div>
          <div className={`bg-gradient-to-br ${hubBoxGradient} rounded-2xl p-7 text-white`}>
            <h3 className="text-lg font-bold mb-5">{hubsTitle}</h3>
            <div className="space-y-3">
              {hubs.map(hub => (
                <div key={hub.code} className="flex items-center justify-between border-b border-white/10 pb-2 last:border-0 last:pb-0">
                  <span className="text-white/70 text-sm">{hub.name}</span>
                  <span className="bg-white/20 text-white text-xs font-bold px-2 py-0.5 rounded">{hub.code}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Cabins */}
      <section className="bg-gray-50 py-12 sm:py-16 px-4">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-10">
            <h2 className="text-2xl sm:text-3xl font-extrabold text-gray-900 mb-2">{cabinsHeading}</h2>
            <p className="text-gray-500">{cabinsSubheading}</p>
          </div>
          <div className={`grid sm:grid-cols-2 ${cabins.length >= 4 ? 'lg:grid-cols-4' : 'lg:grid-cols-3'} gap-5`}>
            {cabins.map(cabin => (
              <div key={cabin.name} className={`border-2 ${cabin.color} rounded-2xl p-5 flex flex-col`}>
                <div className="text-3xl mb-3">{cabin.icon}</div>
                <span className={`text-xs font-bold px-2 py-0.5 rounded-full self-start mb-3 ${cabin.badge}`}>{cabin.name}</span>
                <ul className="space-y-2 flex-grow">
                  {cabin.features.map(f => (
                    <li key={f} className="text-xs text-gray-700 flex items-start gap-1.5">
                      <span className="text-gray-400 font-bold shrink-0">✓</span> {f}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Popular Routes */}
      <section className="max-w-5xl mx-auto px-4 py-12 sm:py-16">
        <div className="text-center mb-10">
          <h2 className="text-2xl sm:text-3xl font-extrabold text-gray-900 mb-2">{routesHeading}</h2>
          <p className="text-gray-500">{routesSubheading}</p>
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          {routes.map((route, i) => (
            <a
              key={i}
              href={searchUrl}
              className="flex items-center justify-between bg-white border border-gray-100 shadow-sm hover:shadow-md rounded-xl px-5 py-4 transition-shadow group"
            >
              <div>
                <div className="flex items-center gap-2 text-sm font-semibold text-gray-800">
                  <span>{route.from}</span>
                  <span className="text-gray-400">→</span>
                  <span>{route.to}</span>
                </div>
                <p className="text-xs text-gray-400 mt-0.5">✈️ {route.duration}</p>
              </div>
              <div className="flex items-center gap-2">
                <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${
                  route.tag === 'Deal' ? 'bg-green-100 text-green-700' :
                  route.tag === 'International' ? 'bg-purple-100 text-purple-700' :
                  route.tag === 'Business' ? 'bg-amber-100 text-amber-700' :
                  route.tag === 'Vacation' ? 'bg-pink-100 text-pink-700' :
                  'bg-blue-100 text-blue-700'
                }`}>{route.tag}</span>
                <span className="text-gray-300 group-hover:text-blue-500 transition-colors">→</span>
              </div>
            </a>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className={`bg-gradient-to-r ${ctaGradient} py-12 px-4`}>
        <div className="max-w-3xl mx-auto text-center text-white">
          <h2 className="text-2xl sm:text-3xl font-extrabold mb-3">{ctaHeading}</h2>
          <p className="text-white/80 mb-7 text-base">{ctaDesc}</p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <a href={TEL} className="inline-flex items-center justify-center gap-2 bg-white text-gray-900 font-bold px-8 py-3.5 rounded-xl hover:bg-gray-100 transition-colors">
              📞 Call {TFN}
            </a>
            <a href={searchUrl} className="inline-flex items-center justify-center gap-2 border-2 border-white/30 hover:border-white/60 text-white font-semibold px-8 py-3.5 rounded-xl transition-colors">
              Search Flights Online →
            </a>
          </div>
          <p className="text-white/60 text-xs mt-5">No booking fees · Free 24/7 support · Best price guarantee</p>
        </div>
      </section>

      {/* Why Book */}
      <section className="max-w-5xl mx-auto px-4 py-12 sm:py-16">
        <div className="text-center mb-10">
          <h2 className="text-2xl sm:text-3xl font-extrabold text-gray-900 mb-2">Why Book {name} Flights with TheAirlineFares?</h2>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {whyBookItems.map(item => (
            <div key={item.title} className="bg-white border border-gray-100 rounded-2xl p-5 shadow-sm">
              <div className="text-3xl mb-3">{item.icon}</div>
              <h3 className="font-bold text-gray-900 mb-1.5">{item.title}</h3>
              <p className="text-sm text-gray-500 leading-relaxed">{item.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-gray-50 py-12 sm:py-16 px-4">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-10">
            <h2 className="text-2xl sm:text-3xl font-extrabold text-gray-900 mb-2">Frequently Asked Questions</h2>
            <p className="text-gray-500">Everything you need to know about booking {name} flights.</p>
          </div>
          <div className="space-y-3">
            {faqs.map((faq, i) => (
              <div key={i} className="bg-white border border-gray-100 rounded-xl overflow-hidden shadow-sm">
                <button
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  className="w-full text-left px-5 py-4 flex items-start justify-between gap-3 hover:bg-gray-50 transition-colors"
                >
                  <span className="font-semibold text-gray-800 text-sm leading-snug">{faq.q}</span>
                  <span className={`text-blue-500 font-bold text-lg shrink-0 transition-transform ${openFaq === i ? 'rotate-45' : ''}`}>+</span>
                </button>
                {openFaq === i && (
                  <div className="px-5 pb-4 text-sm text-gray-600 leading-relaxed border-t border-gray-50">{faq.a}</div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="max-w-5xl mx-auto px-4 py-10 text-center">
        <p className="text-gray-500 text-sm">
          Have more questions about {name} flights?{' '}
          <a href={TEL} className="text-blue-600 font-semibold hover:underline">{TFN}</a>
          {' '}— we are available 24/7.
        </p>
      </section>
    </div>
  );
}
